import { useEffect } from 'react'
import DisplayMode from 'interfaces/DisplayMode'
import {
  addEtages,
  changeSurfaceAuSolMin,
  changeSurfaceAuSolMax,
  changePiscine,
  changeSurfaceHabitable,
  changeSurfaceTerrainMax,
  changeSurfaceTerrainMin,
  changeTypeBien,
  resetFormulaire,
  deleteEtages,
  toggleDoNotUseSurfaceHabitable as toggleDoNotUseSurfaceHabitableAction,
  updateLocations,
  updateParticularShapeOrGarage,
  setCalculViaConfigurateurAvance,
  showCalculator,
  changeBatiMitoyen,
  toggleDisplayMode as toggleDisplayModeAction,
} from 'redux/features/bienAVendre/slice'
import {
  makeSelectLocations,
  makeSelectTypebien,
  makeSelectSurfaceTerrainMin,
  makeSelectSurfaceTerrainMax,
  makeSelectSurfaceHabitable,
  makeSelectSurfaceHabitableMin,
  makeSelectSurfaceHabitableMax,
  makeSelectSurfaceAuSolMin,
  makeSelectSurfaceAuSolMax,
  makeSelectPiscine,
  makeSelectBatiMitoyen,
  makeSelectEtages,
  makeSelectDoNotUseSurfaceHabitable,
  makeSelectParticularShapeOrGarage,
  makeSelectDisplayMode,
} from 'redux/features/bienAVendre/selectors'
import { idle as idleFavoris } from 'redux/features/favoris/slice'
import { toggleFilterResult as toggleFilterResultAction } from 'redux/features/filterResult/slice'
import Location from 'interfaces/Location'
import { useAppDispatch, useAppSelector } from 'redux/hooks'
import { APISearchBien } from 'interfaces/API'
import Etage from 'interfaces/Etage'
import {
  Status as StatusParcelle,
  idleParcelle,
} from 'redux/features/parcelle/slice'
import { makeSelectSearchCircle } from 'redux/features/map/selectors'
import useParcelle from './useParcelle'
import useParcelleNavigation from './useParcelleNavigation'
import BienAVendreFilters from '../enums/BienAVendreFilters'
import { makeSelectBienAVendreNameFilter } from '../redux/features/filterResult/selectors'

interface ReturnProps {
  locations: Location[]
  typeBien: number | null
  surfaceTerrainMin: number
  surfaceTerrainMax: number
  surfaceHabitable: number | null
  surfaceHabitableMin: number | null
  surfaceHabitableMax: number | null
  surfaceAuSolMin: number | null
  surfaceAuSolMax: number | null
  etages: Etage[]
  batiMitoyen: number | null
  piscine: number | null
  doNotUseSurfaceHabitable: boolean
  particularShapeOrGarage: number | null
  setLocation: (newLocations: Location[]) => void
  setTypeDeBien: (newValue: number) => void
  setPiscine: (newValue: number) => void
  setSurfaceHabitable: (newValue: number) => void
  toggleDoNotUseSurfaceHabitable: () => void
  incrementNombreEtage: () => void
  decrementNombreEtage: () => void
  setParticularShapeOrGarage: (newValue: number) => void
  setSurfaceAuSolMin: (newValue: number) => void
  setSurfaceAuSolMax: (newValue: number) => void
  setSurfaceTerrainMin: (newValue: number) => void
  setSurfaceTerrainMax: (newValue: number) => void
  setBatiMitoyen: (newValue: number) => void
  enableCalculViaConfigurateurAvance: () => void
  resetCalculViaConfigurateurAvance: () => void
  onSubmitForm: () => void
  statusParcelle: StatusParcelle
  errorParcelle: string | null
  nextParcelle: () => void
  previousParcelle: () => void
  isNextAvailable: boolean
  isPreviousAvailable: boolean
  displayMode: DisplayMode
  onEditClick: () => void
  onNouveauCiblageClick: () => void
  onEffacerCriteresClick: () => void
  toggleResultFilterAction: (filter: BienAVendreFilters) => void
  bienAVendreResultFilter: BienAVendreFilters | null
}

const useBienAVendre = (): ReturnProps => {
  const locations = useAppSelector(makeSelectLocations)
  const typeBien = useAppSelector(makeSelectTypebien)
  const surfaceHabitable = useAppSelector(makeSelectSurfaceHabitable)
  const surfaceTerrainMin = useAppSelector(makeSelectSurfaceTerrainMin)
  const surfaceTerrainMax = useAppSelector(makeSelectSurfaceTerrainMax)
  const surfaceHabitableMin = useAppSelector(makeSelectSurfaceHabitableMin)
  const surfaceHabitableMax = useAppSelector(makeSelectSurfaceHabitableMax)
  const surfaceAuSolMin = useAppSelector(makeSelectSurfaceAuSolMin)
  const surfaceAuSolMax = useAppSelector(makeSelectSurfaceAuSolMax)
  const piscine = useAppSelector(makeSelectPiscine)
  const batiMitoyen = useAppSelector(makeSelectBatiMitoyen)
  const etages = useAppSelector(makeSelectEtages)
  const doNotUseSurfaceHabitable = useAppSelector(
    makeSelectDoNotUseSurfaceHabitable
  )
  const bienAVendreResultFilter = useAppSelector(
    makeSelectBienAVendreNameFilter
  )
  const particularShapeOrGarage = useAppSelector(
    makeSelectParticularShapeOrGarage
  )
  const displayMode = useAppSelector(makeSelectDisplayMode)
  const dispatch = useAppDispatch()

  const searchCircle = useAppSelector(makeSelectSearchCircle)
  const {
    statusParcelle,
    errorParcelle,
    fetchSearchBien,
    makeMapFitAllResults,
  } = useParcelle()
  const {
    isNextAvailable,
    isPreviousAvailable,
    nextParcelle,
    previousParcelle,
  } = useParcelleNavigation()

  const setLocation = (newLocations: Location[]) =>
    dispatch(updateLocations(newLocations))

  const setTypeDeBien = (newValue: number) => dispatch(changeTypeBien(newValue))

  const setPiscine = (value: number) => {
    let newPiscine = null
    if (value === 0) newPiscine = true
    if (value === 1) newPiscine = false
    dispatch(changePiscine(newPiscine))
  }

  const setSurfaceHabitable = (newValue: number) =>
    dispatch(changeSurfaceHabitable(newValue))

  const toggleDoNotUseSurfaceHabitable = () =>
    dispatch(toggleDoNotUseSurfaceHabitableAction())

  const incrementNombreEtage = () => dispatch(deleteEtages())

  const decrementNombreEtage = () => dispatch(addEtages())

  const setParticularShapeOrGarage = (newValue: number) =>
    dispatch(updateParticularShapeOrGarage(newValue))

  const setSurfaceAuSolMin = (newValue: number) =>
    dispatch(changeSurfaceAuSolMin(newValue))

  const setSurfaceAuSolMax = (newValue: number) =>
    dispatch(changeSurfaceAuSolMax(newValue))

  const setSurfaceTerrainMin = (newValue: number) =>
    dispatch(changeSurfaceTerrainMin(newValue))

  const setSurfaceTerrainMax = (newValue: number) =>
    dispatch(changeSurfaceTerrainMax(newValue))

  const setBatiMitoyen = (value: number) => {
    let mitoyen = null
    if (value === 0) mitoyen = true
    if (value === 1) mitoyen = false
    dispatch(changeBatiMitoyen(mitoyen))
  }

  const submitForm = async () => {
    dispatch(idleParcelle())
    dispatch(idleFavoris())
    const searchData: APISearchBien = {
      cityCode: null,
      searchZone: null,
      typeBien: typeBien || 0,
      piscine,
      aireParcelle: {
        min: surfaceTerrainMin,
        max: surfaceTerrainMax,
      },
      surfaceHabitable:
        doNotUseSurfaceHabitable ||
        surfaceHabitableMin === null ||
        surfaceHabitableMax === null
          ? null
          : {
              min: surfaceHabitableMin,
              max: surfaceHabitableMax,
            },
      surfaceAuSol:
        surfaceAuSolMin === null || surfaceAuSolMax === null
          ? null
          : {
              min: surfaceAuSolMin,
              max: surfaceAuSolMax,
            },
      batiMitoyen,
    }
    if (searchCircle !== undefined) {
      searchData.searchZone = {
        rayon: searchCircle.radius,
        centre: {
          latitude: searchCircle.center.lat,
          longitude: searchCircle.center.lng,
        },
      }
    } else searchData.cityCode = locations.map(({ codeInsee }) => codeInsee)
    fetchSearchBien(searchData)
  }

  const enableCalculViaConfigurateurAvance = () =>
    dispatch(setCalculViaConfigurateurAvance(true))

  const resetCalculViaConfigurateurAvance = () =>
    dispatch(setCalculViaConfigurateurAvance(false))

  const toggleDisplayMode = (newDisplayMode?: DisplayMode) => {
    dispatch(toggleDisplayModeAction(newDisplayMode))
  }

  const convertBooleanToNumber = (value: boolean | null) => {
    if (value === true) return 0
    if (value === false) return 1
    return 2
  }

  const onEditClick = () => {
    toggleDisplayMode({ type: 'normal' })
    // Clear before next search to avoid crash
    dispatch(idleParcelle())
    dispatch(idleFavoris())
  }

  const onNouveauCiblageClick = () => {
    dispatch(idleParcelle())
    dispatch(idleFavoris())
    dispatch(resetFormulaire())
  }

  const onEffacerCriteresClick = () => {
    dispatch(idleParcelle())
    dispatch(idleFavoris())
    dispatch(resetFormulaire())
  }

  const toggleResultFilterAction = (filter: BienAVendreFilters): void => {
    dispatch(toggleFilterResultAction(filter))
  }

  useEffect(() => {
    if (statusParcelle === StatusParcelle.Succeeded) {
      makeMapFitAllResults()
    }
    if (
      statusParcelle === StatusParcelle.ShowParcelleResults ||
      statusParcelle === StatusParcelle.ShowModalBeaucoupDeResultats
    ) {
      toggleDisplayMode({ type: 'synthese' })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps -- Will trigger at unwanted times
  }, [statusParcelle])

  useEffect(() => {
    dispatch(showCalculator(particularShapeOrGarage === 0))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Triggers an error
  }, [particularShapeOrGarage])

  return {
    locations,
    typeBien,
    surfaceHabitable,
    surfaceTerrainMin,
    surfaceTerrainMax,
    surfaceHabitableMin,
    surfaceHabitableMax,
    surfaceAuSolMin,
    surfaceAuSolMax,
    etages,
    batiMitoyen: convertBooleanToNumber(batiMitoyen),
    piscine: convertBooleanToNumber(piscine),
    doNotUseSurfaceHabitable,
    particularShapeOrGarage,
    setLocation,
    setTypeDeBien,
    setPiscine,
    setSurfaceHabitable,
    toggleDoNotUseSurfaceHabitable,
    incrementNombreEtage,
    decrementNombreEtage,
    setParticularShapeOrGarage,
    setSurfaceAuSolMin,
    setSurfaceAuSolMax,
    setSurfaceTerrainMin,
    setSurfaceTerrainMax,
    setBatiMitoyen,
    enableCalculViaConfigurateurAvance,
    resetCalculViaConfigurateurAvance,
    onSubmitForm: submitForm,
    statusParcelle,
    errorParcelle,
    nextParcelle,
    previousParcelle,
    isNextAvailable,
    isPreviousAvailable,
    displayMode,
    onEditClick,
    onNouveauCiblageClick,
    onEffacerCriteresClick,
    toggleResultFilterAction,
    bienAVendreResultFilter,
  }
}

export default useBienAVendre
